import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  IconButton,
  InputAdornment,
  Alert,
  Stack,
  Box,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Facebook,
  Instagram,
  Twitter,
  LinkedIn,
  Link,
} from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageIcon from "@mui/icons-material/Image";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import { useParams } from "react-router-dom";
import axios from "axios";
import { LoadingButton } from "@mui/lab";
import { SketchPicker } from "react-color";
import { toast } from "react-toastify";

import useTemplateSettings from "../../hooks/useTemplateSettings";
import MuPb from "../../widgets/MuPb";
import {
  getAuthHeader,
  getDbHeader,
  getErrorResponse,
  toastError,
} from "../../utils/util";
import ImageCropperModal from "../../widgets/ImageCropperModal";
import FullImageModal from "../../widgets/FullImageModal";
import SimpleYesNoDialog from "../../widgets/SimpleYesNoDialog";
import BackBtn from "../../widgets/BackBtn";
import { API_ENDPOINT } from "../../utils/constants";

const defaultFooterLinks = {
  fb: "",
  in: "",
  x: "",
  ln: "",
  siteName: "",
  siteLink: "",
};

// --- New Reusable Internal Component for Image Previews ---
const ImagePreviewCard = ({
  label,
  url,
  onView,
  onChange,
  showDelete,
  onDelete,
  isDeleting,
  deleteDisabled,
}) => {
  const [error, setError] = useState(false);

  // Reset error state if URL changes
  useEffect(() => {
    setError(false);
  }, [url]);

  return (
    <Paper
      variant="outlined"
      sx={{
        display: "flex",
        alignItems: "center",
        p: 1.5,
        gap: 2,
        borderRadius: 2,
        borderColor: "divider",
        backgroundColor: "background.paper",
        mb: 2,
      }}
    >
      {/* Thumbnail */}
      <Box
        onClick={() => url && onView()}
        sx={{
          width: 64,
          height: 64,
          flexShrink: 0,
          borderRadius: 1,
          backgroundColor: "action.hover",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          cursor: url ? "pointer" : "default",
        }}
      >
        {!url ? (
          <ImageIcon color="disabled" />
        ) : error ? (
          <BrokenImageIcon color="error" />
        ) : (
          <img
            src={url}
            alt={label}
            onError={() => setError(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}
      </Box>

      {/* Info & Labels */}
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: url ? "text.primary" : "text.disabled" }}
        >
          {url ? "Image uploaded" : "No image selected"}
        </Typography>
      </Box>

      {/* Actions */}
      <Stack direction="row" spacing={1} alignItems="center">
        {url && (
          <Tooltip title="View Full Image">
            <IconButton onClick={onView} size="small" color="primary">
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Button
          onClick={onChange}
          variant="outlined"
          startIcon={<AddIcon />}
          size="small"
        >
          {url ? "Change" : "Upload"}
        </Button>
        {showDelete && (
          <Tooltip
            title={deleteDisabled ? "Cannot delete last item" : "Delete"}
          >
            <span>
              <IconButton
                color="error"
                size="small"
                onClick={onDelete}
                disabled={isDeleting || deleteDisabled}
              >
                {isDeleting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <DeleteOutlineIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
    </Paper>
  );
};

const TemplateSettings = () => {
  const { id } = useParams();
  const { data, isLoading, isError, error, refetch } = useTemplateSettings(id);

  const [showImage, setShowImage] = useState(false);
  const [imageToShow, setImageToShow] = useState("");
  const [config, setConfig] = useState({
    name: "",
    formTitle: "",
    headerBanner: "",
    headerBg: "",
    headerSliders: [],
    footerBanner: "",
    footerLogo: "",
    footerLinks: defaultFooterLinks,
    footerBgColor: "#ffffff",
    submitBgColor: "#1976d2",
  });

  const [cropperOpen, setCropperOpen] = useState(false);
  const [propertyType, setPropertyType] = useState("");
  const [aspectRatio, setAspectRatio] = useState(2 / 1);

  const [deleting, setDeleting] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submittingSpecifics, setSubmittingSpecifics] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [submitColorPickerOpen, setSubmitColorPickerOpen] = useState(false);

  const handleUploadSuccess = (response) => {
    toast.success(response.message);
    refetch();
  };

  const handleShowImage = (imgUrl) => {
    setImageToShow(imgUrl);
    setShowImage(true);
  };

  // Sync local state when data loads
  useEffect(() => {
    if (data?.properties) {
      const socialKeys = ["fb", "in", "x", "ln", "siteName", "siteLink"];
      const loadedFooterLinks = { ...defaultFooterLinks };

      if (data.properties.footerLinks) {
        socialKeys.forEach((key) => {
          if (data.properties.footerLinks[key] !== undefined) {
            loadedFooterLinks[key] = data.properties.footerLinks[key];
          }
        });
      }

      setConfig({
        name: data.name || "",
        formTitle: data.formTitle || "",
        headerBanner: data.properties.headerBanner || "",
        headerBg: data.properties.headerBg || "",
        headerSliders: data.properties.headerSliders || [],
        footerBanner: data.properties.footerBanner || "",
        footerLogo: data.properties.footerLogo || "",
        footerLinks: loadedFooterLinks,
        footerBgColor:
          data.properties.footerBgColor ||
          data.properties.footerLinks?.footerBgColor ||
          "#ffffff",
        submitBgColor: data.properties.submitBgColor || "#1976d2",
      });
    }
  }, [data]);

  const handleChange = (path, value) => {
    const paths = path.split(".");
    setConfig((prev) => ({
      ...prev,
      [paths[0]]:
        paths.length === 1 ? value : { ...prev[paths[0]], [paths[1]]: value },
    }));
  };

  const handleSubmitSpecifics = async () => {
    try {
      setSubmittingSpecifics(true);
      const { data: resData } = await axios.post(
        `${API_ENDPOINT}event/update-template-specifics`,
        { formTitle: config.formTitle },
        getAuthHeader(id)
      );
      toast.success(resData.message);
      refetch();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmittingSpecifics(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const { data: resData } = await axios.post(
        `${API_ENDPOINT}event/submit-footer-link`,
        {
          footerLinks: config.footerLinks,
          footerBgColor: config.footerBgColor,
          submitBgColor: config.submitBgColor,
        },
        getAuthHeader(id)
      );
      toast.success(resData.message);
      refetch();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) return <MuPb />;
  if (isError) return <Alert severity="error">{getErrorResponse(error)}</Alert>;

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <SimpleYesNoDialog
        title="Please Confirm"
        message="Are you sure you want to delete this?"
        onYes={async () => {
          try {
            setDeleting(true);
            setDeleteOpen(false);
            const { data: resData } = await axios.delete(
              `${API_ENDPOINT}event/delete-slider-image?imgUrl=${deleteId}`,
              getDbHeader(id)
            );
            toast.success(resData.message);
            refetch();
          } catch (err) {
            toastError(err);
          } finally {
            setDeleting(false);
          }
        }}
        show={deleteOpen}
        setShow={setDeleteOpen}
      />

      <FullImageModal
        imgUrl={imageToShow}
        open={showImage}
        handleClose={() => setShowImage(false)}
      />

      <ImageCropperModal
        open={cropperOpen}
        type={propertyType}
        onClose={() => setCropperOpen(false)}
        aspectRatio={aspectRatio}
        maxSizeKB={1024}
        uploadUrl={`${API_ENDPOINT}event/upload-image-event-settings/${propertyType}${
          propertyType === "properties.headerSliders" ? "?qp=sliders" : ""
        }`}
        headers={{ dbauth: id }}
        onSuccess={handleUploadSuccess}
        onError={(err) => console.error("Upload error:", err)}
      />

      <Typography variant="h5" gutterBottom>
        {config.name}
      </Typography>
      <BackBtn />

      {/* ── Form Details ── */}
      <Paper sx={{ p: 3, mb: 3, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Form Details
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              size="small"
              fullWidth
              label="Form Title"
              value={config.formTitle}
              onChange={(e) => handleChange("formTitle", e.target.value)}
            />
          </Grid>
        </Grid>
        <Stack direction="row" justifyContent="flex-end">
          <LoadingButton
            loading={submittingSpecifics}
            sx={{ mt: 2 }}
            variant="contained"
            color="primary"
            onClick={handleSubmitSpecifics}
            startIcon={<SaveOutlinedIcon />}
          >
            Save
          </LoadingButton>
        </Stack>
      </Paper>

      {/* ── Header Settings ── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Header Settings
        </Typography>

        <ImagePreviewCard
          label="Header Banner"
          url={config.headerBanner}
          onView={() => handleShowImage(config.headerBanner)}
          onChange={() => {
            setPropertyType("properties.headerBanner");
            setAspectRatio(1198 / 199);
            setCropperOpen(true);
          }}
        />

        <ImagePreviewCard
          label="Header Background"
          url={config.headerBg}
          onView={() => handleShowImage(config.headerBg)}
          onChange={() => {
            setPropertyType("properties.headerBg");
            setAspectRatio(1921 / 623);
            setCropperOpen(true);
          }}
        />

        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
          Header Sliders
        </Typography>
        {config.headerSliders.map((slider, index) => (
          <ImagePreviewCard
            key={index}
            label={`Slider Image ${index + 1}`}
            url={slider}
            onView={() => handleShowImage(slider)}
            onChange={() => {
              setPropertyType("properties.headerSliders");
              setAspectRatio(1201 / 301);
              setCropperOpen(true);
            }}
            showDelete={true}
            deleteDisabled={config.headerSliders.length <= 1}
            isDeleting={deleting && deleteId === slider}
            onDelete={() => {
              setDeleteId(slider);
              setDeleteOpen(true);
            }}
          />
        ))}

        <Button
          onClick={() => {
            setPropertyType("properties.headerSliders");
            setAspectRatio(1201 / 301);
            setCropperOpen(true);
          }}
          color="primary"
          startIcon={<AddIcon />}
        >
          Add Slider Image
        </Button>
      </Paper>

      {/* ── Footer Settings ── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Footer Settings
        </Typography>

        <ImagePreviewCard
          label="Footer Banner"
          url={config.footerBanner}
          onView={() => handleShowImage(config.footerBanner)}
          onChange={() => {
            setPropertyType("properties.footerBanner");
            setAspectRatio(601 / 121);
            setCropperOpen(true);
          }}
        />

        <ImagePreviewCard
          label="Footer Logo"
          url={config.footerLogo}
          onView={() => handleShowImage(config.footerLogo)}
          onChange={() => {
            setPropertyType("properties.footerLogo");
            setAspectRatio(425 / 90);
            setCropperOpen(true);
          }}
        />

        {/* ── Colors & Links (Unchanged visually, cleaned code) ── */}
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Footer Background Color
            </Typography>
            <Stack direction="row" alignItems="center" gap={2}>
              <Box
                onClick={() => setColorPickerOpen((prev) => !prev)}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1,
                  border: "1px solid #ccc",
                  backgroundColor: config.footerBgColor,
                  cursor: "pointer",
                  transition: "transform 0.15s",
                  "&:hover": { transform: "scale(1.1)" },
                }}
              />
              <TextField
                size="small"
                label="Hex Code"
                value={config.footerBgColor}
                onChange={(e) => handleChange("footerBgColor", e.target.value)}
                inputProps={{ maxLength: 7 }}
              />
            </Stack>
            {colorPickerOpen && (
              <Box sx={{ mt: 1, position: "relative", zIndex: 10 }}>
                <Box
                  onClick={() => setColorPickerOpen(false)}
                  sx={{ position: "fixed", inset: 0 }}
                />
                <Box sx={{ position: "relative" }}>
                  <SketchPicker
                    color={config.footerBgColor}
                    onChange={(c) => handleChange("footerBgColor", c.hex)}
                  />
                </Box>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Submit Button Color
            </Typography>
            <Stack direction="row" alignItems="center" gap={2}>
              <Box
                onClick={() => setSubmitColorPickerOpen((prev) => !prev)}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1,
                  border: "1px solid #ccc",
                  backgroundColor: config.submitBgColor,
                  cursor: "pointer",
                  transition: "transform 0.15s",
                  "&:hover": { transform: "scale(1.1)" },
                }}
              />
              <TextField
                size="small"
                label="Hex Code"
                value={config.submitBgColor}
                onChange={(e) => handleChange("submitBgColor", e.target.value)}
                inputProps={{ maxLength: 7 }}
              />
            </Stack>
            {submitColorPickerOpen && (
              <Box sx={{ mt: 1, position: "relative", zIndex: 10 }}>
                <Box
                  onClick={() => setSubmitColorPickerOpen(false)}
                  sx={{ position: "fixed", inset: 0 }}
                />
                <Box sx={{ position: "relative" }}>
                  <SketchPicker
                    color={config.submitBgColor}
                    onChange={(c) => handleChange("submitBgColor", c.hex)}
                  />
                </Box>
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              Social Links
            </Typography>
            <Grid container spacing={2}>
              {[
                { key: "fb", label: "Facebook", icon: <Facebook /> },
                { key: "in", label: "Instagram", icon: <Instagram /> },
                { key: "x", label: "Twitter (X)", icon: <Twitter /> },
                { key: "ln", label: "LinkedIn", icon: <LinkedIn /> },
                { key: "siteName", label: "Site Name", icon: <Link /> },
                { key: "siteLink", label: "Site URL", icon: <Link /> },
              ].map((social) => (
                <Grid item xs={12} sm={6} key={social.key}>
                  <TextField
                    size="small"
                    fullWidth
                    label={social.label}
                    value={config.footerLinks[social.key]}
                    onChange={(e) =>
                      handleChange(`footerLinks.${social.key}`, e.target.value)
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {social.icon}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="flex-end">
          <LoadingButton
            loading={submitting}
            sx={{ mt: 3 }}
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            startIcon={<SaveOutlinedIcon />}
          >
            Save
          </LoadingButton>
        </Stack>
      </Paper>
    </Container>
  );
};

export default TemplateSettings;
